import { useContext, useEffect, useRef, useState } from "react";
import sheet from "./bases.json";
import { PageContext } from "./page";
import type { Cutout } from "../types";

/** Their figure standing on the base it is printed on, with the message on it.
 *
 *  Nothing here is drawn by hand. The base is a render of the 3D pipeline's
 *  own mesh (3d_optimizer tools/render_base_preview.py writes the images in
 *  /bases and the numbers in bases.json), from a camera a little above, as
 *  the drawings are, and for the Christmas base a little higher, so its snow
 *  shows. The figure is the drawing itself, cut out of the
 *  transparent background a statue is drawn on (functions lib/cutout.ts),
 *  found by the drawing's url (PageInfo.cutouts); a drawing made before that
 *  has none, and its base is shown on its own. The message is bent onto the
 *  wall here, as they type, by the same steps base_message.build_message_mesh
 *  takes to raise it in plastic: the same font, band, height and curve, the
 *  same 55 degrees either side of the front before long words get smaller.
 *
 *  The footprint is what the pipeline prints: the smallest ellipse round the
 *  whole figure, 1.08 times its width for one figure and 1.17 times for a
 *  couple or group. A front view cannot say how deep a figure is, so its depth
 *  is taken as 0.38 of its height and the nearest of the rendered depths is
 *  used. The plain base is as tall as it prints, 3.35 % of the figure's height,
 *  the nearest of the three rendered heights. The figure stands where the
 *  prints have it: its lowest point a little in front of the top's middle,
 *  moved only as far as keeps every foot on the top (the drawing's own contacts,
 *  Cutout.feet), with a shadow under each foot. Sizes are in millimetres of a
 *  figure FIGURE_MM tall; the canvas fits them. */

type Style = "plain" | "christmas";
interface Shot {
  ratio: number;
  /** Plain bases only: the base's height in millimetres, at the nominal width. */
  heightMm?: number;
  file: string;
  width: number;
  height: number;
  originX: number;
  originY: number;
  sizeX: number;
  sizeZ: number;
}

/** The base's font, once per visit, the first time a Christmas base is shown.
 *  A failed load is forgotten, so the next one asks again; the message is set
 *  in a rounded system face meanwhile. */
const FONT = "MiniME Base";
let fontAsked = false;
function loadBaseFont(): void {
  if (fontAsked || typeof FontFace === "undefined" || !document.fonts) return;
  fontAsked = true;
  const face = new FontFace(FONT, 'url("/fonts/baloo2-extrabold-latin.woff") format("woff")', {
    weight: "800",
    display: "swap",
  });
  face.load().then(
    (loaded) => document.fonts.add(loaded),
    () => {
      fontAsked = false;
    }
  );
}

const FACE = `"MiniME Base", ui-rounded, "Arial Rounded MT Bold", system-ui, sans-serif`;
/** The camera height of each style's images, as the sheet has it. */
const sinOf = (style: Style) => Math.sin((sheet.camera[style] * Math.PI) / 180);
const cosOf = (style: Style) => Math.cos((sheet.camera[style] * Math.PI) / 180);
/** The figure's height in the layout, base not included. */
const FIGURE_MM = 72;
/** Without a figure, the base of one this wide and this tall. */
const BARE_MM = { width: 36, height: 60 };
/** A standing figure's depth, as a share of its height, kept within its width. */
const DEPTH_OF_HEIGHT = 0.38;
/** The printed base is the smallest ellipse round every point of the figure
 *  (constrain_base_to_model), so its width is this many times the figure's: a
 *  single figure, narrow for its height, and a couple or group. */
const spreadFor = (figW: number, figH: number) => (figW / figH < 0.55 ? 1.08 : 1.17);
/** The plain base's height in the print, as a share of the figure's height,
 *  whatever its width. */
const PLAIN_OF_FIGURE = 0.0335;
/** Room left on screen between the feet and either rim of the top, as a share
 *  of the figure's height. */
const RIM = 0.016;
/** Where the prints have the figure's lowest point, in front of the top's
 *  middle (26 orders, at the images' camera): LOW_AT of its height, and
 *  LOW_PER_BACK of the height of its back-most contact more. */
const LOW_AT = 0.022;
const LOW_PER_BACK = 0.17;
/** Without the drawing's own contacts, the back-most one is taken this high, as
 *  a share of the figure's height. */
const ASSUMED_BACK = 0.011;
/** The source raster the message is drawn at before it is bent (px tall). */
const ROWS = 96;

function interp(x: number, xs: readonly number[], ys: readonly number[]): number {
  const n = xs.length;
  if (!n) return 0;
  if (x <= (xs[0] ?? 0)) return ys[0] ?? 0;
  for (let i = 1; i < n; i++) {
    const x1 = xs[i] ?? 0;
    if (x <= x1) {
      const x0 = xs[i - 1] ?? 0;
      const y0 = ys[i - 1] ?? 0;
      const y1 = ys[i] ?? 0;
      return x1 === x0 ? y1 : y0 + ((x - x0) * (y1 - y0)) / (x1 - x0);
    }
  }
  return ys[n - 1] ?? 0;
}

interface Layout {
  style: Style;
  shot: Shot;
  /** Millimetres per pixel of the base's image. */
  mmPerPx: number;
  sizeX: number;
  sizeZ: number;
  /** `bottom` is the lowest point's place in front of the top's middle, as it
   *  shows on screen; `back` the height of the back-most contact above it. */
  figure: { width: number; height: number; bottom: number; back: number } | null;
}

function layoutFor(style: Style, cut: Cutout | null): Layout {
  const figW = cut ? (cut.width / cut.height) * FIGURE_MM : BARE_MM.width;
  const figH = cut ? FIGURE_MM : BARE_MM.height;
  const depth = Math.min(figW, Math.max(figW * 0.35, figH * DEPTH_OF_HEIGHT));
  // The footprint the pipeline fits a base to: the ellipse round all of the figure.
  const spread = spreadFor(figW, figH);
  const dx = figW * spread;
  const dz = depth * spread;
  const shots: Shot[] = sheet.styles[style];
  let near = shots.reduce((best, s) => (Math.abs(s.ratio - dz / dx) < Math.abs(best.ratio - dz / dx) ? s : best)).ratio;
  const sin = sinOf(style);
  // Each contact as [left, right, height above the lowest point] in mm; without
  // the drawing's own, two feet a third of the width apart, front and back.
  const flat = cut?.feet ?? [];
  const given =
    flat.length >= 3
      ? Array.from({ length: Math.floor(flat.length / 3) }, (_, i) => flat.slice(i * 3, i * 3 + 3).map((v) => v * figH))
      : null;
  const feet = given ?? [
    [-figW * 0.32, -figW * 0.05, 0],
    [figW * 0.05, figW * 0.32, ASSUMED_BACK * figH],
  ];
  const back = Math.max(...feet.map((f) => f[2] ?? 0));
  let low = 0;
  if (cut) {
    // The lowest point's place in front of the top's middle that keeps every
    // contact inside the top's ellipse on screen, RIM in from its edge. The
    // first depth (at least the shape's own) where the prints' place fits is
    // taken, else the first where any place does.
    const ratios = [...new Set(shots.map((s) => s.ratio))].sort((a, b) => a - b).filter((r) => r >= near - 0.001);
    const rim = RIM * figH;
    const fitFor = (r: number) => {
      const a = dx / 2 - rim;
      const b = (r * dx * sin) / 2 - rim;
      let lo = -Infinity;
      let hi = Infinity;
      for (const [l = 0, rr = 0, h = 0] of feet) {
        for (const x of [l, rr]) {
          const reach = a > Math.abs(x) ? b * Math.sqrt(1 - (x / a) ** 2) : 0;
          lo = Math.max(lo, h - reach);
          hi = Math.min(hi, h + reach);
        }
      }
      return { lo, hi };
    };
    const target = LOW_AT * figH + LOW_PER_BACK * back;
    const ok =
      ratios.find((r) => {
        const { lo, hi } = fitFor(r);
        return lo <= target && target <= hi;
      }) ??
      ratios.find((r) => {
        const { lo, hi } = fitFor(r);
        return lo <= hi;
      });
    near = ok ?? ratios[ratios.length - 1] ?? near;
    const { lo, hi } = fitFor(near);
    low = lo <= hi ? Math.min(hi, Math.max(lo, target)) : (lo + hi) / 2;
  }
  // Of that shape, the plain base as tall (for its width) as it prints; the
  // Christmas shots have one height only.
  const tall = (PLAIN_OF_FIGURE * figH) / dx;
  const heightOf = (s: Shot) => Math.abs((s.heightMm ?? 0) / s.sizeX - tall);
  const shot = shots.filter((s) => s.ratio === near).reduce((best, s) => (heightOf(s) < heightOf(best) ? s : best));
  const fit = style === "christmas" ? 1 / sheet.christmas.standingFraction : 1;
  const scale = (dx * fit) / shot.sizeX;
  return {
    style,
    shot,
    mmPerPx: scale / sheet.pxPerMm,
    sizeX: shot.sizeX * scale,
    sizeZ: shot.sizeZ * scale,
    figure: cut ? { width: figW, height: figH, bottom: low, back } : null,
  };
}

/** The scene's extent in millimetres, y down from the middle of the base's top. */
function boundsOf(l: Layout): { x: number; y: number; w: number; h: number } {
  const bx = -l.shot.originX * l.mmPerPx;
  const by = -l.shot.originY * l.mmPerPx;
  let x0 = bx;
  let y0 = by;
  let x1 = bx + l.shot.width * l.mmPerPx;
  let y1 = by + l.shot.height * l.mmPerPx;
  if (l.figure) {
    x0 = Math.min(x0, -l.figure.width / 2);
    x1 = Math.max(x1, l.figure.width / 2);
    y0 = Math.min(y0, l.figure.bottom - l.figure.height);
  }
  const pad = 1.5;
  return { x: x0 - pad, y: y0 - pad, w: x1 - x0 + 2 * pad, h: y1 - y0 + 2 * pad };
}

/** The message raised on the Christmas base's wall, drawn onto `ctx` (whose
 *  units are the scene's millimetres) as base_message lays it out. */
function drawMessage(ctx: CanvasRenderingContext2D, text: string, l: Layout, faint: boolean): void {
  const c = sheet.christmas;
  const sizeY = Math.sqrt(l.sizeX * l.sizeZ);
  const bottomY = -c.standingHeight * sizeY;
  const [low = 0, high = 0] = c.band;
  const bandMm = (high - low) * sizeY;
  const midN = (low + high) / 2;
  const rMid = interp(midN, c.profileY, c.profileR);
  const semiX = l.sizeX * rMid;
  const semiZ = l.sizeZ * rMid;

  // Arc length from the front, as _arc_table: the letters keep their widths
  // around the curve, so the ones at the sides are seen narrower.
  const steps = 256;
  const phis = Array.from({ length: steps }, (_, i) => (i / (steps - 1)) * (Math.PI / 2));
  const speeds = phis.map((phi) => Math.hypot(semiX * Math.cos(phi), semiZ * Math.sin(phi)));
  const arcs = [0];
  for (let i = 1; i < steps; i++) {
    arcs.push((arcs[i - 1] ?? 0) + (((speeds[i] ?? 0) + (speeds[i - 1] ?? 0)) / 2) * ((phis[i] ?? 0) - (phis[i - 1] ?? 0)));
  }
  const maxWidth = 2 * interp((sheet.letters.maxHalfAngleDeg * Math.PI) / 180, phis, arcs);

  // The text's own ink, as _render_mask crops it.
  const src = document.createElement("canvas");
  const g = src.getContext("2d");
  if (!g) return;
  g.font = `800 100px ${FACE}`;
  const m = g.measureText(text);
  const inkW = m.actualBoundingBoxLeft + m.actualBoundingBoxRight;
  const inkH = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent;
  if (!(inkW > 0 && inkH > 0)) return;
  const k = ROWS / inkH;
  const cols = Math.ceil(inkW * k);
  src.width = cols + 2;
  src.height = ROWS + 2;
  g.font = `800 ${100 * k}px ${FACE}`;
  // The snow's colour in the light the render gives the wall there.
  g.fillStyle = `rgb(${c.letterRgb.join(",")})`;
  g.fillText(text, 1 + m.actualBoundingBoxLeft * k, 1 + m.actualBoundingBoxAscent * k);

  const aspect = inkW / inkH;
  const letter = Math.min(bandMm * sheet.letters.heightFill, maxWidth / aspect);
  const width = letter * aspect;
  const lowN = midN - letter / sizeY / 2;
  const spanN = letter / sizeY;
  const relief = sheet.letters.reliefMm;
  // A point of the letters' face (u along the wall from the front, v up them)
  // bent onto the wall, stood off it by the relief, and seen by the camera.
  const at = (u: number, v: number): [number, number] => {
    const phi = interp(Math.abs(u), arcs, phis) * Math.sign(u);
    const yN = lowN + v * spanN;
    const r = interp(yN, c.profileY, c.profileR);
    const sx = l.sizeX * r;
    const sz = l.sizeZ * r;
    const nx = Math.sin(phi) / sx;
    const nz = Math.cos(phi) / sz;
    const nl = Math.hypot(nx, nz) || 1;
    const x = sx * Math.sin(phi) + (nx / nl) * relief;
    const y = bottomY + yN * sizeY;
    const z = sz * Math.cos(phi) + (nz / nl) * relief;
    return [x, -(y * cosOf("christmas") - z * sinOf("christmas"))];
  };

  // Column by column: each one-pixel strip of the flat text onto the sliver
  // of wall it lands on.
  const layer = document.createElement("canvas");
  layer.width = ctx.canvas.width;
  layer.height = ctx.canvas.height;
  const lc = layer.getContext("2d");
  if (!lc) return;
  lc.setTransform(ctx.getTransform());
  for (let i = 0; i < cols; i++) {
    const u0 = (i / cols - 0.5) * width;
    const u1 = ((i + 1) / cols - 0.5) * width;
    const [tx, ty] = at(u0, 1);
    const [tx1, ty1] = at(u1, 1);
    const [bx, by] = at(u0, 0);
    lc.save();
    lc.transform(tx1 - tx, ty1 - ty, (bx - tx) / ROWS, (by - ty) / ROWS, tx, ty);
    lc.drawImage(src, 1 + i, 1, 1, ROWS, 0, 0, 1.6, ROWS);
    lc.restore();
  }
  // The wall turns away from the light towards the sides, and the letters with it.
  lc.save();
  lc.globalCompositeOperation = "source-atop";
  const shade = lc.createLinearGradient(-semiX, 0, semiX, 0);
  shade.addColorStop(0, "rgba(120, 30, 30, 0.14)");
  shade.addColorStop(0.4, "rgba(120, 30, 30, 0)");
  shade.addColorStop(0.65, "rgba(120, 30, 30, 0)");
  shade.addColorStop(1, "rgba(120, 30, 30, 0.38)");
  lc.fillStyle = shade;
  lc.fillRect(-semiX * 1.2, -sizeY * 2, semiX * 2.4, sizeY * 4);
  lc.restore();

  // Raised: each letter's edge throws a thin shadow down and to the right.
  const edge = document.createElement("canvas");
  edge.width = layer.width;
  edge.height = layer.height;
  const ec = edge.getContext("2d");
  if (!ec) return;
  ec.drawImage(layer, 0, 0);
  ec.globalCompositeOperation = "source-in";
  ec.fillStyle = "rgba(70, 8, 10, 0.55)";
  ec.fillRect(0, 0, edge.width, edge.height);

  const px = ctx.getTransform().a;
  ctx.save();
  ctx.resetTransform();
  ctx.globalAlpha = faint ? 0.5 : 1;
  ctx.drawImage(edge, px * 0.18, px * 0.26);
  ctx.drawImage(layer, 0, 0);
  ctx.restore();
}

/** A shadow right under each foot, paw and tail where it meets the
 *  top. The figure's own lower edges (the bottom of its solid shape, a sliver
 *  deep, in the lowest part of the figure), darkened, softened and drawn under
 *  it a hair lower: only compositing, so a figure from another origin, whose
 *  pixels cannot be read, works as well. */
function contactShadow(ctx: CanvasRenderingContext2D, img: HTMLImageElement, f: { width: number; height: number; bottom: number }): void {
  const BAND = 0.09; // the lowest share of the figure looked at
  const EDGE = 0.012; // how deep a sliver of each lower edge
  const px = ctx.getTransform().a;
  const pad = f.height * 0.03;
  const w = Math.max(2, Math.round((f.width + 2 * pad) * px));
  const h = Math.max(2, Math.round((f.height * BAND + 2 * pad) * px));
  const o = document.createElement("canvas");
  o.width = w;
  o.height = h;
  const g = o.getContext("2d");
  if (!g) return;
  const sy = img.naturalHeight * (1 - BAND);
  const sh = img.naturalHeight * BAND;
  const at = (lift: number) => g.drawImage(img, 0, sy, img.naturalWidth, sh, pad * px, (pad - lift) * px, f.width * px, f.height * BAND * px);
  at(0);
  g.globalCompositeOperation = "destination-out";
  at(f.height * EDGE);
  g.globalCompositeOperation = "source-in";
  g.fillStyle = "rgb(38, 30, 34)";
  g.fillRect(0, 0, w, h);
  // Softened by going small and back.
  const soft = (k: number) => {
    const t = document.createElement("canvas");
    t.width = Math.max(1, Math.round(w / k));
    t.height = Math.max(1, Math.round(h / k));
    t.getContext("2d")?.drawImage(o, 0, 0, t.width, t.height);
    return t;
  };
  const x = -f.width / 2 - pad;
  const y = f.bottom - f.height * BAND - pad + f.height * 0.004;
  ctx.save();
  ctx.imageSmoothingQuality = "high";
  ctx.globalAlpha = 0.5;
  ctx.drawImage(soft(Math.max(2, f.height * 0.006 * px)), x, y, f.width + 2 * pad, f.height * BAND + 2 * pad);
  ctx.globalAlpha = 0.35;
  ctx.drawImage(soft(Math.max(3, f.height * 0.018 * px)), x, y + f.height * 0.004, f.width + 2 * pad, f.height * BAND + 2 * pad);
  ctx.restore();
}

/** An image once it has loaded, and whether it failed to. */
function useImage(url: string | null): { image: HTMLImageElement | null; failed: boolean } {
  const [state, setState] = useState<{ image: HTMLImageElement | null; failed: boolean }>({ image: null, failed: false });
  useEffect(() => {
    setState({ image: null, failed: false });
    if (!url) return;
    let live = true;
    const img = new Image();
    img.decoding = "async";
    img.onload = () => live && setState({ image: img, failed: false });
    img.onerror = () => live && setState({ image: null, failed: true });
    img.src = url;
    return () => {
      live = false;
    };
  }, [url]);
  return state;
}

/** Whether a drawing has its figure alone to stand on the base (PageInfo.cutouts). */
export function useCutout(drawingUrl: string | null): Cutout | null {
  const page = useContext(PageContext);
  return (drawingUrl && page?.cutouts?.[drawingUrl]) || null;
}

export default function BaseScene({
  drawingUrl,
  style,
  text,
  placeholder,
  label,
  className = "xbase-mock xscene",
  fitHeight,
  onReady,
  onFail,
}: {
  /** Their drawing, whose figure alone stands on the base where it has one. */
  drawingUrl: string | null;
  style: Style;
  /** The message on a Christmas base, cleaned; empty shows the placeholder, fainter. */
  text: string;
  placeholder: string;
  label: string;
  className?: string;
  /** At most this share of the screen's height (the hero's 56vh), narrower if need be. */
  fitHeight?: number;
  /** Drawn, figure and all: the hero takes its skeleton away. */
  onReady?: () => void;
  /** Their figure could not be loaded: the hero shows their drawing instead. */
  onFail?: () => void;
}) {
  const cut = useCutout(drawingUrl);
  const figure = useImage(cut?.url ?? null);
  // Laid out by the cutout's own size, known before it loads; without it if it cannot be.
  const layout = layoutFor(style, cut && !figure.failed ? cut : null);
  const base = useImage(`/bases/${layout.shot.file}`);
  const wrap = useRef<HTMLElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [room, setRoom] = useState({ width: 0, height: 0 });
  const [fonts, setFonts] = useState(0);
  const told = useRef(false);

  useEffect(() => {
    if (style !== "christmas") return;
    loadBaseFont();
    const set = document.fonts;
    if (!set) return;
    const again = () => setFonts((n) => n + 1);
    set.addEventListener("loadingdone", again);
    return () => set.removeEventListener("loadingdone", again);
  }, [style]);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const measure = () => setRoom({ width: el.clientWidth, height: window.innerHeight });
    measure();
    window.addEventListener("resize", measure);
    if (typeof ResizeObserver === "undefined") return () => window.removeEventListener("resize", measure);
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => {
      watch.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    if ((figure.failed || base.failed) && cut) onFail?.();
  }, [figure.failed, base.failed, cut, onFail]);

  const bounds = boundsOf(layout);
  const tall = bounds.h / bounds.w;
  const cssWidth = Math.floor(fitHeight && room.height ? Math.min(room.width, (room.height * fitHeight) / tall) : room.width);
  const cssHeight = Math.round(cssWidth * tall);
  // A figure on its way is waited for, so the base does not jump when it lands.
  const waiting = !!cut && !figure.image && !figure.failed;

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx || !cssWidth || !base.image || waiting) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    el.width = Math.round(cssWidth * dpr);
    el.height = Math.round(cssHeight * dpr);
    const s = (cssWidth * dpr) / bounds.w;
    ctx.setTransform(s, 0, 0, s, -bounds.x * s, -bounds.y * s);
    ctx.imageSmoothingQuality = "high";

    const { shot, mmPerPx } = layout;
    ctx.drawImage(base.image, -shot.originX * mmPerPx, -shot.originY * mmPerPx, shot.width * mmPerPx, shot.height * mmPerPx);
    if (style === "christmas") drawMessage(ctx, text || placeholder, layout, !text);
    if (layout.figure && figure.image) {
      const f = layout.figure;
      contactShadow(ctx, figure.image, f);
      // Over the whole stance, a lighter round glow on the top.
      const rx = f.width * 0.36;
      const ry = Math.max(rx * 0.12, rx * (layout.sizeZ / layout.sizeX) * sinOf(style) * 1.3);
      ctx.save();
      ctx.translate(0, f.bottom - f.back / 2);
      ctx.scale(1, ry / rx);
      const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
      glow.addColorStop(0, "rgba(40, 30, 32, 0.22)");
      glow.addColorStop(1, "rgba(40, 30, 32, 0)");
      ctx.fillStyle = glow;
      ctx.fillRect(-rx, -rx, rx * 2, rx * 2);
      ctx.restore();
      ctx.drawImage(figure.image, -f.width / 2, f.bottom - f.height, f.width, f.height);
    }
    if (!told.current) {
      told.current = true;
      onReady?.();
    }
  });

  return (
    <figure ref={wrap} className={className} role="img" aria-label={label}>
      <div
        className="xscene-box"
        style={cssWidth ? { width: cssWidth, height: cssHeight } : { aspectRatio: `${bounds.w} / ${bounds.h}` }}
      >
        <canvas ref={canvas} aria-hidden="true" data-fonts={fonts} />
      </div>
    </figure>
  );
}
